// src/utils/pageSelector.js
// Smart page selection for Preview/Share with 50-page limit
// Preserves narrative arc: opening, chapter intros, closing, + best distributed content

const MAX_SHARE_PAGES = 50;

// Page type categories
const SPECIAL_TYPES = new Set(['cover', 'stats', 'route', 'map', 'timeline', 'journal-short']);
const INTRO_TYPES = new Set(['step-intro-left', 'step-intro-right']);

/**
 * Check if a page has user-added content (indicates engagement/importance)
 */
function hasUserContent(page) {
  const caption = page?.meta?.caption || page?.caption || '';
  const journalText = page?.meta?.journalText || page?.meta?.text || '';
  return caption.trim().length > 0 || journalText.trim().length > 0;
}

/**
 * Count photos on a page (more photos = more engaging)
 */
function getPhotoCount(page) {
  if (Array.isArray(page?.photos)) return page.photos.length;
  if (Array.isArray(page?.items)) return page.items.length;
  return 0;
}

/**
 * Calculate a priority score for a page (higher = more likely to be included)
 */
function calculatePageScore(page, pageIndex, totalPages) {
  let score = 0;
  
  // User-edited pages get highest priority
  if (hasUserContent(page)) {
    score += 100;
  }
  
  // Photo count bonus (collages are more engaging)
  const photoCount = getPhotoCount(page);
  score += photoCount * 10; // 1 photo = 10, 4 photos = 40
  
  // Template bonuses for visually rich layouts
  const templateKey = page?.templateKey || page?.type || '';
  if (templateKey.includes('collage')) score += 15;
  if (templateKey.includes('hero')) score += 5;
  if (templateKey === 'journal-right') score += 20; // Journal pages have stories
  
  // Small position bonus for even distribution (middle pages slightly favored to avoid clustering)
  const normalizedPos = pageIndex / Math.max(1, totalPages - 1);
  const distanceFromEdge = Math.min(normalizedPos, 1 - normalizedPos);
  score += distanceFromEdge * 5; // 0-2.5 bonus
  
  return score;
}

/**
 * Get unique step IDs from pages
 */
function getUniqueStepIds(pages) {
  const stepIds = new Set();
  pages.forEach(page => {
    if (page?.stepId) stepIds.add(page.stepId);
  });
  return Array.from(stepIds);
}

/**
 * Group pages by their stepId
 */
function groupPagesByStep(pages) {
  const groups = new Map();
  
  pages.forEach((page, index) => {
    const stepId = page?.stepId || '__no_step__';
    if (!groups.has(stepId)) {
      groups.set(stepId, []);
    }
    groups.get(stepId).push({ page, originalIndex: index });
  });
  
  return groups;
}

/**
 * Select best pages for sharing, respecting the 50-page limit.
 * 
 * Algorithm:
 * 1. Always include: first 5 pages, last 2 pages
 * 2. Include first 2 pages of each step/chapter (if budget allows)
 * 3. Fill remaining with highest-scored pages (user edits, more photos)
 * 
 * @param {Array} pages - Full album pages array
 * @param {number} maxPages - Maximum pages to include (default 50)
 * @returns {Object} { selectedPages, wasLimited, originalCount, selectedCount }
 */
export function selectPagesForShare(pages, maxPages = MAX_SHARE_PAGES) {
  if (!Array.isArray(pages) || pages.length === 0) {
    return { 
      selectedPages: [], 
      wasLimited: false, 
      originalCount: 0, 
      selectedCount: 0 
    };
  }
  
  // If under limit, return all pages
  if (pages.length <= maxPages) {
    return { 
      selectedPages: pages, 
      wasLimited: false, 
      originalCount: pages.length, 
      selectedCount: pages.length 
    };
  }
  
  console.log(`[PageSelector] Album has ${pages.length} pages, limiting to ${maxPages}`);
  
  // Track which indices are selected
  const selectedIndices = new Set();
  
  // === RULE 1: First 5 pages (cover, stats, map, opening content) ===
  const firstN = Math.min(5, pages.length);
  for (let i = 0; i < firstN; i++) {
    selectedIndices.add(i);
  }
  console.log(`[PageSelector] Added first ${firstN} pages`);
  
  // === RULE 2: Last 2 pages (timeline, closing journal) ===
  const lastN = Math.min(2, pages.length);
  for (let i = pages.length - lastN; i < pages.length; i++) {
    selectedIndices.add(i);
  }
  console.log(`[PageSelector] Added last ${lastN} pages`);
  
  // === RULE 3: First 2 pages of each step/chapter ===
  const stepGroups = groupPagesByStep(pages);
  const stepIds = Array.from(stepGroups.keys()).filter(id => id !== '__no_step__');
  
  // Calculate how many step pages we can afford
  const fixedPages = selectedIndices.size;
  const remainingBudget = maxPages - fixedPages;
  const maxStepPages = 2; // Try 2 pages per step first
  const totalStepPagesNeeded = stepIds.length * maxStepPages;
  
  let pagesPerStep = maxStepPages;
  if (totalStepPagesNeeded > remainingBudget * 0.7) {
    // If step pages would use >70% of remaining budget, reduce to 1 per step
    pagesPerStep = 1;
    console.log(`[PageSelector] Reducing to ${pagesPerStep} page per step (${stepIds.length} steps)`);
  }
  
  // Add first N pages of each step
  stepIds.forEach(stepId => {
    const stepPages = stepGroups.get(stepId) || [];
    // Prioritize step-intro pages, then other pages
    const sortedStepPages = [...stepPages].sort((a, b) => {
      const aIsIntro = INTRO_TYPES.has(a.page?.type) ? 1 : 0;
      const bIsIntro = INTRO_TYPES.has(b.page?.type) ? 1 : 0;
      return bIsIntro - aIsIntro; // Intros first
    });
    
    for (let i = 0; i < Math.min(pagesPerStep, sortedStepPages.length); i++) {
      if (selectedIndices.size < maxPages) {
        selectedIndices.add(sortedStepPages[i].originalIndex);
      }
    }
  });
  console.log(`[PageSelector] After step pages: ${selectedIndices.size} selected`);
  
  // === RULE 4: Fill remaining with best-scored pages ===
  if (selectedIndices.size < maxPages) {
    // Score all unselected pages
    const unselectedWithScores = pages
      .map((page, index) => ({
        page,
        index,
        score: calculatePageScore(page, index, pages.length)
      }))
      .filter(item => !selectedIndices.has(item.index))
      .sort((a, b) => b.score - a.score); // Highest score first
    
    // Add best pages until we hit the limit
    for (const item of unselectedWithScores) {
      if (selectedIndices.size >= maxPages) break;
      selectedIndices.add(item.index);
    }
    console.log(`[PageSelector] After filling: ${selectedIndices.size} selected`);
  }
  
  // === Build final array in original order ===
  const sortedIndices = Array.from(selectedIndices).sort((a, b) => a - b);
  const selectedPages = sortedIndices.map(i => pages[i]);
  
  console.log(`[PageSelector] Final selection: ${selectedPages.length} of ${pages.length} pages`);
  
  // Log what types were selected
  const typeCounts = {};
  selectedPages.forEach(p => {
    const t = p?.type || p?.templateKey || 'unknown';
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  console.log('[PageSelector] Selected page types:', typeCounts);
  
  return {
    selectedPages,
    wasLimited: true,
    originalCount: pages.length,
    selectedCount: selectedPages.length
  };
}

/**
 * Get a human-readable summary of the selection
 */
export function getSelectionSummary(result) {
  if (!result.wasLimited) {
    return null; // No limit applied
  }
  return `Sharing ${result.selectedCount} highlights from your ${result.originalCount}-page album`;
}

export default selectPagesForShare;
